import * as Sentry from "@sentry/react";
import { FormikValues } from "formik";
import { RootState } from "../app/store";
import { AxiosError } from "axios";
import { HolderData } from "../reducers/securitiesReducer";


// SENSITIVE FIELDS CONFIGURATION

// List of field names or patterns that contain sensitive data
export const SENSITIVE_FIELDS = [
  "socialSecurityNumber",
  "taxId",
  "documentNumber",
  "passportNumber",
  "proofOfAddress",
  "documentFront",
  "documentBack",
  "documentFrontDNI",
  "documentBackDNI",
  "documentFrontPASSPORT",
  "documentBackPASSPORT",
] as const;

// Patterns to identify phone fields
const PHONE_FIELD_PATTERNS = ["phone", "Phone", "phoneNumber", "PhoneNumber"];

// Patterns to identify file fields
const FILE_FIELD_PATTERNS = [
  "document",
  "proof",
  "file",
  "Document",
  "Proof",
  "File",
];

// SANITIZATION FUNCTIONS

// Masks a Social Security Number
const maskSSN = (value: string): string => {
  if (!value || typeof value !== "string") return "[REDACTED]";
  return "***-**-****";
};

// Masks a Tax ID / CUIT / Document Number
const maskTaxId = (value: string): string => {
  if (!value || typeof value !== "string") return "[REDACTED]";
  // Show only structure, not content
  return value.replace(/\d/g, "*");
};

// Masks a phone number, keeping only last 4 digits
const maskPhone = (value: string): string => {
  if (!value || typeof value !== "string") return "[REDACTED]";
  if (value.length <= 4) return "****";
  return "*".repeat(value.length - 4) + value.slice(-4);
};

// Sanitizes a file object, keeping only metadata
const sanitizeFile = (
  file: unknown
): { type: string; name: string; size?: number } | string => {
  if (!file) return "[NO_FILE]";

  if (typeof file === "object" && file !== null) {
    const fileObj = file as { name?: string; type?: string; size?: number };
    return {
      type: "FILE",
      name: fileObj.name || "[UNKNOWN]",
      size: fileObj.size,
    };
  }

  return "[FILE_DATA_REDACTED]";
};

// Checks if a field name matches any sensitive pattern
const isSensitiveField = (fieldName: string): boolean => {
  const lowerFieldName = fieldName.toLowerCase();

  // Check exact matches
  if (
    SENSITIVE_FIELDS.some(
      (sf) => lowerFieldName === sf.toLowerCase() || fieldName.includes(sf)
    )
  ) {
    return true;
  }

  // Check phone patterns
  if (PHONE_FIELD_PATTERNS.some((pattern) => fieldName.includes(pattern))) {
    return true;
  }

  // Check file patterns
  if (FILE_FIELD_PATTERNS.some((pattern) => fieldName.includes(pattern))) {
    return true;
  }

  return false;
};

// Sanitizes a single value based on field name
const sanitizeValue = (fieldName: string, value: unknown): unknown => {
  if (value === null || value === undefined) return value;

  const lowerFieldName = fieldName.toLowerCase();

  // SSN sanitization
  if (lowerFieldName.includes("socialsecuritynumber")) {
    return maskSSN(String(value));
  }

  // Tax ID / Document Number sanitization
  if (
    lowerFieldName.includes("taxid") ||
    lowerFieldName.includes("documentnumber") ||
    lowerFieldName.includes("passportnumber")
  ) {
    return maskTaxId(String(value));
  }

  // Phone sanitization
  if (PHONE_FIELD_PATTERNS.some((pattern) => fieldName.includes(pattern))) {
    return maskPhone(String(value));
  }

  // File sanitization
  if (
    FILE_FIELD_PATTERNS.some((pattern) => fieldName.includes(pattern)) ||
    (typeof value === "object" &&
      value !== null &&
      ("base64String" in value || "name" in value))
  ) {
    return sanitizeFile(value);
  }

  return value;
};

/**
 * Sanitizes form values, removing or masking sensitive data
 * @param values - Formik values object
 * @param parentKey - Used for recursion to track nested field names
 * @returns Sanitized values object safe to send to Sentry or an object with a sanitization error flag and the error
 */
export const sanitizeFormValues = (
  values: FormikValues,
  parentKey = ""
): FormikValues => {
  try {
    if (!values || typeof values !== "object") return values;

    const sanitized: FormikValues = {};

    for (const [key, value] of Object.entries(values)) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;

      // Handle nested objects (but not arrays or file-like objects)
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        !("base64String" in value) &&
        !("name" in value && "type" in value)
      ) {
        sanitized[key] = sanitizeFormValues(value as FormikValues, fullKey);
      }
      // Handle arrays
      else if (Array.isArray(value)) {
        sanitized[key] = value.map((item, index) => {
          if (typeof item === "object" && item !== null) {
            return sanitizeFormValues(item, `${fullKey}[${index}]`);
          }
          return isSensitiveField(fullKey) ? sanitizeValue(fullKey, item) : item;
        });
      }
      // Handle sensitive fields
      else if (isSensitiveField(key) || isSensitiveField(fullKey)) {
        sanitized[key] = sanitizeValue(key, value);
      }
      // non-sensitive values are sent without sanitization
      else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  } catch (error) {
    console.error("[Sentry] sanitizeFormValues failed internally:", error);
    return { "[sanitization_error]": true, error: error };
  }
};

// BREADCRUMB HELPER FUNCTIONS

export type BreadcrumbCategory =
  | "navigation"
  | "form"
  | "api"
  | "validation"
  | "user";

/**
 * Adds a breadcrumb for step navigation
 */
export const addStepBreadcrumb = (
  stepId: number | string,
  action: "enter" | "exit" | "complete" | "error",
  data?: Record<string, unknown>
): void => {
  Sentry.addBreadcrumb({
    category: "navigation",
    message: `Step ${stepId}: ${action}`,
    level: action === "error" ? "error" : "info",
    data: {
      stepId,
      action,
      timestamp: new Date().toISOString(),
      ...data,
    },
  });
};

/**
 * Adds a breadcrumb for substep navigation
 */
export const addSubStepBreadcrumb = (
  stepId: number | string,
  subStepId: number,
  action: "enter" | "exit" | "complete",
  data?: Record<string, unknown>
): void => {
  Sentry.addBreadcrumb({
    category: "navigation",
    message: `Step ${stepId} - SubStep ${subStepId}: ${action}`,
    level: "info",
    data: {
      stepId,
      subStepId,
      action,
      timestamp: new Date().toISOString(),
      ...data,
    },
  });
};

/**
 * Adds a breadcrumb for form submission attempts
 */
export const addFormSubmitBreadcrumb = (
  stepId: number | string,
  success: boolean,
  errorMessage?: string
): void => {
  Sentry.addBreadcrumb({
    category: "form",
    message: success
      ? `Form submitted successfully on step ${stepId}`
      : `Form submission failed on step ${stepId}`,
    level: success ? "info" : "error",
    data: {
      stepId,
      success,
      errorMessage,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Adds a breadcrumb for API calls
 */
export const addApiBreadcrumb = (
  endpoint: string,
  method: string,
  success: boolean,
  statusCode?: number
): void => {
  Sentry.addBreadcrumb({
    category: "api",
    message: `${method} ${endpoint}: ${success ? "success" : "failed"}`,
    level: success ? "info" : "error",
    data: {
      endpoint,
      method,
      success,
      statusCode,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Adds a breadcrumb for validation errors (used for pattern tracking)
 */
export const addValidationBreadcrumb = (
  fieldName: string,
  errorMessage: string,
  attemptCount: number
): void => {
  Sentry.addBreadcrumb({
    category: "validation",
    message: `Validation error on ${fieldName} (attempt ${attemptCount})`,
    level: attemptCount >= 3 ? "warning" : "info",
    data: {
      fieldName,
      errorMessage,
      attemptCount,
      timestamp: new Date().toISOString(),
    },
  });
};

// CONTEXT HELPER FUNCTIONS

/**
 * Sets the securities context in Sentry with relevant state information
 */
export const setSecuritiesContext = (state: RootState): void => {
  const { securities } = state;

  Sentry.setContext("securities_state", {
    currentHolder: securities.currentHolder,
    accountHolders: securities.accountHolders,
    language: securities.language,
    USCitizen: securities.USCitizen,
    beneficiariesCount: securities.beneficiariesCount,
    completedSteps: securities.stepData?.completedSteps,
    currentStep: securities.stepData?.step,
    currentSubStep: securities.stepData?.subStep,
    reachedSteps: securities.stepData?.reachedSteps,
    holdersDataCount: securities.holdersData?.length || 0,
    draftUUID: securities.draftUUID,
  });
};

/**
 * Sets tags for filtering in Sentry
 */
export const setSecuritiesTags = (
  stepId: number | string,
  currentHolder: number
): void => {
  Sentry.setTag("onboarding.type", "SECURITIES");
  Sentry.setTag("step.id", String(stepId));
  Sentry.setTag("holder.number", String(currentHolder));
};

/**
 * Sets enriched user context in Sentry
 */
export const setEnrichedUser = async (
  email: string,
  language: "es" | "en",
  additionalData?: Record<string, unknown>
): Promise<void> => {
  Sentry.setUser({
    email: email, // Sentry masks this by default in Session Replay
    segment: "securities",
    language,
    ...additionalData,
  });
};

// VALIDATION ERROR TRACKING

// Threshold for reporting validation error patterns to Sentry
export const VALIDATION_ERROR_THRESHOLD = 5;

export const shouldReportValidationPattern = (
  currentCount: number,
  threshold = VALIDATION_ERROR_THRESHOLD
): boolean => {
  return currentCount === threshold;
};

/**
 * Reports a validation error pattern to Sentry.
 */
export const reportValidationErrorPattern = (
  fieldName: string,
  errorMessage: string,
  attemptCount: number,
  value?: unknown
): void => {
  // Add breadcrumb
  addValidationBreadcrumb(fieldName, errorMessage, attemptCount);

  // Capture as warning message
  Sentry.captureMessage(`Repeated validation error pattern detected`, {
    level: "warning",
    tags: {
      "validation.field": fieldName,
      "validation.pattern": "repeated_error",
    },
    extra: {
      fieldName,
      errorMessage,
      attemptCount,
      value: value ? sanitizeValue(fieldName, value) : undefined,
    },
  });
};

// API ERROR EXTRACTION

export interface ApiErrorDetails {
  isAxiosError: boolean;
  endpoint: string | null;
  method: string | null;
  statusCode: number | null;
  statusText: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  responseData: unknown;
  requestData: unknown;
}

/**
 * Checks if an error is an Axios error
 */
const isAxiosError = (error: unknown): boolean => {
  return (
    error !== null &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as AxiosError).isAxiosError === true
  );
};

/**
 * Extracts detailed API error information from an Axios error
 */
export const extractApiErrorDetails = (error: unknown): ApiErrorDetails => {
  const defaultDetails: ApiErrorDetails = {
    isAxiosError: false,
    endpoint: null,
    method: null,
    statusCode: null,
    statusText: null,
    errorCode: null,
    errorMessage: null,
    responseData: null,
    requestData: null,
  };

  if (!isAxiosError(error)) {
    return defaultDetails;
  }

  const axiosError = error as AxiosError<any>;

  const response = axiosError.response;
  const config = axiosError.config;

  return {
    isAxiosError: true,
    endpoint: config?.url || null,
    method: config?.method?.toUpperCase() || null,
    statusCode: response?.status || null,
    statusText: response?.statusText || null,
    errorCode: response?.data?.code || response?.data?.error || null,
    errorMessage:
      response?.data?.message ||
      response?.data?.error_description ||
      axiosError.message ||
      null,
    responseData: response?.data || null,
    // requestData: temporarily null to avoid sanitizeFormValues in Stage 0.
    // Reactivate in Stage 3 with sanitization (see captureSecuritiesError).
    requestData: null,
  };
};

/**
 * Adds a detailed API error breadcrumb
 */
export const addApiErrorBreadcrumb = (
  details: ApiErrorDetails,
  context?: { stepId?: number | string; action?: string }
): void => {

  if (details.isAxiosError) {
    Sentry.addBreadcrumb({
      category: "api",
      message: `API Error: ${details.method} ${details.endpoint} - ${details.statusCode} ${details.errorCode || details.statusText}`,
      level: "error",
      data: {
        endpoint: details.endpoint,
        method: details.method,
        statusCode: details.statusCode,
        errorCode: details.errorCode,
        errorMessage: details.errorMessage,
        stepId: context?.stepId,
        action: context?.action,
        timestamp: new Date().toISOString(),
      },
    });
  } else {
    Sentry.addBreadcrumb({
      category: "error",
      message: `Non-API Error: ${details instanceof Error ? details.message : "Unknown error"}`,
      level: "error",
      data: {
        stepId: context?.stepId,
        action: context?.action,
        timestamp: new Date().toISOString(),
      },
    });
  }
};

// CAPTURE EXCEPTION HELPER FUNCTIONS

interface CaptureSecuritiesErrorOptions {
  error: unknown;
  stepId: number | string;
  stepName: string;
  currentHolder: number;
  formValues?: FormikValues;
  requestInfo?: {
    url?: string;
    method?: string;
    body?: unknown;
    documentType?: string;
  };
  state?: RootState;
}

/**
 * Captures an exception with minimal securities context.
 */
export const captureSecuritiesError = ({
  error,
  stepId,
  stepName,
  currentHolder,
  // formValues, requestInfo, state — temporarily ignored.
}: CaptureSecuritiesErrorOptions): void => {
  const api = extractApiErrorDetails(error);

  Sentry.captureException(error, {
    tags: {
      "onboarding.type": "SECURITIES",
      "step.id": String(stepId),
      "holder.number": String(currentHolder),
      ...(api.isAxiosError && {
        "api.endpoint": api.endpoint ?? "unknown",
        "api.status_code": String(api.statusCode ?? "unknown"),
        ...(api.errorCode && { "api.error_code": api.errorCode }),
      }),
    },
    extra: {
      stepName,
      ...(api.isAxiosError && {
        api: {
          endpoint: api.endpoint,
          method: api.method,
          statusCode: api.statusCode,
          errorCode: api.errorCode,
          errorMessage: api.errorMessage,
        },
      }),
    },
  });

  // Stage 1 — uncomment when stage 0 is stable to add more context in breadcrumbs (no sensitive data):
  // addFormSubmitBreadcrumb(stepId, false, api.errorMessage ?? (error instanceof Error ? error.message : "Unknown"));

  // Stage 2 — uncomment when stage 1 is stable to add more context in breadcrumbs (with sanitization):
  // if (state) setSecuritiesContext(state);
  // (incluir api.responseData en extras)

  // Stage 3 — last stage, include sanitized form values and request info in the captured exception:
  // const sanitizedValues = formValues ? sanitizeFormValues(formValues) : null;
  // const sanitizedRequestInfo = requestInfo
  //   ? { ...requestInfo, body: requestInfo.body ? sanitizeFormValues(requestInfo.body as FormikValues) : undefined }
  //   : null;
};

/**
 * Captures a Sentry info event when the user attempts to submit the securities preconfirmation.
 *
 * Stage 0: only structural metadata (holders count, steps sended).
 * Without formValues — used in stage 3 with sanitization if needed
 */
export const captureSecuritiesSubmitAttempt = ({
  accountHolders,
  holdersData,
}: {
  accountHolders: number;
  holdersData: HolderData[];
}): void => {
  Sentry.captureMessage("Securities preconfirmation submit attempt", {
    level: "info",
    tags: {
      "onboarding.type": "SECURITIES",
      "onboarding.status": "submit_attempt",
    },
    extra: {
      accountHolders,
      holdersCount: holdersData.length,
      holdersStructure: holdersData.map((h) => ({
        currentHolder: h?.currentHolder ?? null,
        hasFormValues: !!h?.formValues,
        sendedStepsCount: h?.sendedSteps?.length ?? 0,
        sendedStepsTypes: h?.sendedSteps?.map((s) => s["@type"]) ?? [],
      })),
    },
  });

  // Stage 3 — uncomment when stage 2 is stable to add sanitized form values:
  // holdersStructure includes: formValues: h.formValues ? sanitizeFormValues(h.formValues) : null
};

